import { redirect, type LoaderFunctionArgs } from 'react-router';

import { holdsTier } from '@shared/auth/capabilities';
import { bootstrapSession } from '@shared/auth/client';
import { toNextParam } from '@shared/auth/next-target';
import { holdsConsoleRole, useSessionStore, type AccountRole } from '@shared/auth/session';
import { requirementFor, visibleNavGroups } from '@shared/chrome/nav';

/**
 * The guard chain, in order:
 *
 *     sessionInMemory? → silentRefreshOnceOn401 → me() → roleGate
 *
 * **A client-side route guard is never an authorization boundary.** This bundle
 * is public by construction; every read and every write is authorized by the
 * owning backend on the request itself, and the origin a call arrives from is
 * never an authorization input. A guard that hides a route is correct; a guard
 * trusted to protect data is a defect.
 *
 * A missing or invalid session goes to `/login?next=…`. A **valid** session
 * holding no console role goes to `/login` too, with the reason stated — not to
 * a 404 and not to an empty shell. This is the one place the console departs
 * from the platform's 404-not-403 posture, and the reason is that the posture
 * exists to avoid advertising a resource's *shape*: here there is no shape to
 * hide, because every console section is closed to that account.
 *
 * A session that **does** hold a console role but not the role one destination
 * needs is a different case and gets a different answer: it is redirected to a
 * destination it can use, not signed out. The navigation already hides that
 * entry; this covers the address bar, and the two read the same requirement from
 * the same table so they cannot disagree.
 */
export async function consoleLoader({ request }: LoaderFunctionArgs): Promise<null> {
  const url = new URL(request.url);
  const next = toNextParam({ pathname: url.pathname, search: url.search });

  const authenticated = await bootstrapSession();
  if (!authenticated) throw redirect(`/login?next=${next}`);

  if (!holdsConsoleRole(useSessionStore.getState().role)) {
    throw redirect(`/login?next=${next}&reason=role`);
  }

  // The navigation hides what this account cannot use; typing the address is the
  // other way in, and it lands on the same answer rather than on a surface whose
  // every read is refused.
  const { capabilities } = useSessionStore.getState();
  const requirement = requirementFor(url.pathname);
  if (requirement && !holdsTier(capabilities, requirement.service, requirement.tier)) {
    throw redirect(firstReachable(url.pathname, next));
  }

  return null;
}

/**
 * Where to send an operator who addressed a destination they cannot open.
 *
 * The **first entry the navigation is still drawing**, never a fixed route: a
 * hard-coded fallback is a destination with a requirement of its own, and one
 * that this account also fails redirects into itself forever. Reading the same
 * filtered list the sidebar renders makes that impossible by construction — the
 * target is chosen from what passed the check.
 *
 * An account the filter leaves with nothing holds a console role and no console
 * surface, which is the `reason=role` case the sign-in screen already explains.
 */
function firstReachable(pathname: string, next: string): string {
  const [group] = visibleNavGroups(useSessionStore.getState().capabilities);
  const entry = group?.entries[0];

  if (!entry || entry.to === pathname) return `/login?next=${next}&reason=role`;
  return entry.to;
}

/**
 * For the two routes that additionally require `admin`. It does **not**
 * pre-compute the backend's answer: the route's own reads meet the service's
 * `403` and the console renders its error surface, re-bootstraps `/me`, and
 * re-evaluates. Roles are eventually consistent by design.
 */
export function requireAccountRole(
  roles: readonly AccountRole[],
): (args: LoaderFunctionArgs) => Promise<null> {
  return async (args) => {
    await consoleLoader(args);

    const { role } = useSessionStore.getState();
    if (role && !roles.includes(role)) {
      const url = new URL(args.request.url);
      throw redirect(
        `/login?next=${toNextParam({ pathname: url.pathname, search: url.search })}&reason=role`,
      );
    }

    return null;
  };
}
